import moment from "moment-timezone";
import {
  BroadcastStateType,
  type AudienceFilterInputType,
  type BroadcastInputType,
} from "@/types/graphql/partner.generated";
import {
  audienceIsValid,
  emptyAudience,
  type AutomationDetails,
  type MarketingLimits,
} from "@/partner/entities/marketing";

export interface BroadcastFormState {
  name: string;
  emailTemplateId: string | null;
  /** Зрители платформы входят в аудиторию (audience не null). */
  viewersEnabled: boolean;
  audience: AudienceFilterInputType;
  contactListIds: string[];
  sendMode: "now" | "at";
  scheduledAt: moment.Moment | null;
}

/**
 * Начальное состояние формы из рассылки. Условия копируются без
 * __typename: иначе Apollo отправил бы его во входном типе и сервер
 * отверг бы мутацию.
 */
export function fromAutomation(
  automation: AutomationDetails
): BroadcastFormState {
  const audience: AudienceFilterInputType = automation.audience
    ? {
        match: automation.audience.match,
        conditions: automation.audience.conditions.map((condition) => ({
          field: condition.field,
          op: condition.op,
          boolValue: condition.boolValue ?? null,
          numberValue: condition.numberValue ?? null,
          stringValues: condition.stringValues ?? null,
        })),
      }
    : emptyAudience();
  // «В дату и время» показываем только пока рассылку ещё можно
  // перенести или отправить: у черновика и запланированной. Отправленная
  // рассылка тоже хранит scheduledAt (бэкенд пишет в него now() при
  // немедленной отправке), но карточка расписания там задизейблена, и
  // показывать в ней выбранный радиобаттон "в дату и время" неверно.
  const canShowScheduledAt =
    automation.state === BroadcastStateType.DraftType ||
    automation.state === BroadcastStateType.ScheduledType;
  return {
    name: automation.name,
    emailTemplateId: automation.emailTemplateId ?? null,
    viewersEnabled: Boolean(automation.audience),
    audience,
    contactListIds: [...automation.contactListIds],
    sendMode: automation.scheduledAt && canShowScheduledAt ? "at" : "now",
    scheduledAt: automation.scheduledAt ? moment(automation.scheduledAt) : null,
  };
}

export function toBroadcastInput(
  state: BroadcastFormState
): BroadcastInputType {
  return {
    name: state.name.trim(),
    emailTemplateId: state.emailTemplateId,
    audience: state.viewersEnabled ? state.audience : null,
    contactListIds: state.contactListIds,
    scheduledAt:
      state.sendMode === "at" ? (state.scheduledAt?.toDate() ?? null) : null,
  };
}

/** Ключ перевода причины, по которой рассылку нельзя отправить, или null. */
export function sendIssue(
  state: BroadcastFormState,
  now: moment.Moment,
  limits: MarketingLimits | null
): string | null {
  if (!state.name.trim()) return "broadcasts.form.errors.name";
  if (!state.emailTemplateId) {
    return "marketingErrors.BROADCAST_EMAIL_REQUIRED";
  }
  if (!state.viewersEnabled && state.contactListIds.length === 0) {
    return "marketingErrors.BROADCAST_AUDIENCE_REQUIRED";
  }
  if (state.viewersEnabled && !audienceIsValid(state.audience, limits)) {
    return "marketingErrors.AUDIENCE_FILTER_INVALID";
  }
  if (
    state.sendMode === "at" &&
    (!state.scheduledAt || state.scheduledAt.isBefore(now))
  ) {
    return "marketingErrors.BROADCAST_SCHEDULE_IN_PAST";
  }
  return null;
}

/** Снимок формы для сравнения «есть ли несохранённые правки». */
export const serializeForm = (state: BroadcastFormState): string =>
  JSON.stringify(toBroadcastInput(state));
